import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

const EMPTY = {
  course_id: "", date: "", time: "", room: "", notes: "",
  booking_deadline: "", max_students: "", examiner_teacher_ids: [],
};

export default function ExamSessionFormDialog({ open, onOpenChange, mode = "create", initial, onSubmit, submitting }) {
  const [form, setForm] = useState(EMPTY);
  const { data: courses = [] } = useQuery({
    queryKey: ["courses-exam"],
    queryFn: async () => (await api.get("/courses", { params: { is_seminar: false } })).data,
    enabled: open,
  });
  const { data: teachers = [] } = useQuery({
    queryKey: ["teachers-exam"],
    queryFn: async () => (await api.get("/teachers")).data,
    enabled: open,
  });

  useEffect(() => {
    if (open) setForm(initial ? {
      ...EMPTY, ...initial,
      max_students: initial.max_students ?? "",
      examiner_teacher_ids: initial.examiner_teacher_ids || [],
    } : EMPTY);
  }, [open, initial]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggle = (id) => setForm((p) => ({
    ...p,
    examiner_teacher_ids: p.examiner_teacher_ids.includes(id) ? p.examiner_teacher_ids.filter((x) => x !== id) : [...p.examiner_teacher_ids, id],
  }));

  function submit(e) {
    e.preventDefault();
    const p = { ...form };
    p.max_students = p.max_students === "" ? null : parseInt(p.max_students);
    ["time", "room", "notes", "booking_deadline"].forEach((k) => { if (!p[k]) p[k] = null; });
    onSubmit(p);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Nuovo appello" : "Modifica appello"}</DialogTitle>
          <DialogDescription>Apri una sessione d&apos;esame per un corso. Gli studenti potranno prenotarsi dal portale.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="text-xs">Corso</Label>
            <Select value={form.course_id || ""} onValueChange={(v) => set("course_id", v)} disabled={mode === "edit"}>
              <SelectTrigger data-testid="exam-course"><SelectValue placeholder="Seleziona corso…"/></SelectTrigger>
              <SelectContent>
                {courses.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} {c.year_level ? `· ${c.year_level}° anno` : ""} {c.cfu ? `· ${c.cfu} CFU` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Data appello</Label>
            <Input type="date" required data-testid="exam-date" value={form.date} onChange={(e) => set("date", e.target.value)}/>
          </div>
          <div>
            <Label className="text-xs">Ora</Label>
            <Input type="time" value={form.time || ""} onChange={(e) => set("time", e.target.value)}/>
          </div>
          <div>
            <Label className="text-xs">Aula</Label>
            <Input value={form.room || ""} onChange={(e) => set("room", e.target.value)} placeholder="Aula 1"/>
          </div>
          <div>
            <Label className="text-xs">Posti max (opz.)</Label>
            <Input type="number" min="1" value={form.max_students} onChange={(e) => set("max_students", e.target.value)}/>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Scadenza prenotazione (data e ora)</Label>
            <Input type="datetime-local" value={form.booking_deadline ? form.booking_deadline.slice(0, 16) : ""} onChange={(e) => set("booking_deadline", e.target.value ? new Date(e.target.value).toISOString() : "")}/>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Commissione esaminatrice</Label>
            <div className="mt-1.5 flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border border-stone-200 rounded-md bg-stone-50/50">
              {teachers.length === 0 ? (
                <span className="text-xs text-stone-500">Nessun docente disponibile</span>
              ) : teachers.map((t) => {
                const active = form.examiner_teacher_ids.includes(t.id);
                return (
                  <button
                    key={t.id} type="button" onClick={() => toggle(t.id)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors duration-150 ${active ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white text-stone-700 border-stone-200 hover:border-[#1C352D]"}`}
                  >{t.first_name} {t.last_name}</button>
                );
              })}
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Note</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)}/>
          </div>
          <DialogFooter className="col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={submitting || !form.course_id} data-testid="exam-submit" className="bg-[#1C352D] hover:bg-[#264A3E]">
              {submitting ? "Salvataggio…" : mode === "create" ? "Apri appello" : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
